import { Ethnicity, NHSEthnicityCategory } from '~/api/result';
import { BMIGraphParams } from '../pages/TestListPage/TestResultModal/Measurements/BMI';

/**
 * Maps ethnicity into its corresponding NHS category
 * @param ethnicity based on subject data
 * @returns an NHSEthnicityCategory
 */
export const getEthnicityCategory = (ethnicity: Ethnicity | null): NHSEthnicityCategory => {
  switch (ethnicity) {
    case 'White':
    case 'Other White Background':
    case 'British':
    case 'Irish':
    case 'Not Stated':
    case 'Not Recorded':
      return 'White';
    case 'Other Asian':
    case 'Indian':
    case 'Pakistani':
    case 'Bangladeshi':
    case 'Chinese':
      return 'Asian or Asian British';
    case 'Black African':
    case 'Black Caribbean':
    case 'Other Black':
    case 'Caribbean':
      return 'Black, African, Caribbean or Black British';
    case 'Other Mixed':
    case 'Other Ethnic Group':
    case 'White And Asian Mixed':
    case 'White And Black African Mixed':
    case 'White And Black Caribbean Mixed':
    default:
      return 'Other Ethnic Group';
  }
};

const bmiGraphSignificantPoints = (ethnicity: Ethnicity) => {
  const category = getEthnicityCategory(ethnicity);
  return {
    domainStart: 13.5,
    breakpoints:
      category === 'Asian or Asian British' ||
      category === 'Black, African, Caribbean or Black British'
        ? [18.5, 23, 27.5]
        : [18.5, 25, 30],
    domainEnd: 35,
    domainMax: 50,
  };
};

/**
 * Calculates BMI status based on range the provided value lies in.
 * @param value BMI value
 * @param ethnicity based on subject data
 * @returns BMI status
 */

export const getStatus = (value: number, ethnicity: Ethnicity | null) => {
  const category = getEthnicityCategory(ethnicity);

  const underweightLimit = 18.5;
  let healthyLimit = 25;
  let overweightLimit = 30;
  // let obeseLimit = 30+

  if (
    category === 'Asian or Asian British' ||
    category === 'Black, African, Caribbean or Black British'
  ) {
    healthyLimit = 23;
    overweightLimit = 27.5;
    // obeseLimit = 27.5+;
  }

  if (value < underweightLimit) {
    return 'Underweight';
  }
  if (value < healthyLimit) {
    return 'Healthy';
  }
  if (value < overweightLimit) {
    return 'Overweight';
  }
  return 'Obese';
};

/**
 * helper method that performs value cutoff in case provided input is out of allowed range
 * and returns props needed for UI display
 * @param bmiValue real BMI input
 * @param ethnicity based on subject data
 * @returns {BMIGraphParams}
 */

export const getGraphParams = (bmiValue: number, ethnicity: Ethnicity): BMIGraphParams => {
  const { domainStart, domainEnd, domainMax, breakpoints } = bmiGraphSignificantPoints(ethnicity);
  // by default, graph is plotted from domainStart. In case the provided value is bellow it,
  // the start point is lowered as well in order to plot it.
  const startPoint = Math.min(Math.floor(bmiValue), domainStart);

  // if value is larger than domainMax, we limit it to that point
  if (bmiValue > domainMax) {
    return {
      graphPoints: [startPoint, ...breakpoints, domainMax],
      resultLabel: `> ${domainMax}`,
      resultValue: domainMax,
    };
  }
  return {
    graphPoints: [
      startPoint,
      ...breakpoints,
      // by default, graph is plotted to domainEnd. In case the provided value is above it,
      // the end point is increased as well in order to plot it.
      Math.max(Math.ceil(bmiValue), domainEnd),
    ],
    resultLabel: bmiValue.toFixed(1),
    resultValue: bmiValue,
  };
};
